import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { DiaryEntry, LogEntry } from '../types'

interface NewLogEntry {
  production_id: string
  date_watched: string
  rating: 1 | 2 | 3 | 4 | 5 | null
  review_text: string | null
}

export async function deleteLogEntry(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('log_entries').delete().eq('id', id)
  if (error) return { error: error.message }
  return { error: null }
}

export async function updateLogEntry(
  id: string,
  fields: Partial<Pick<LogEntry, 'production_id' | 'date_watched' | 'rating' | 'review_text' | 'user_photo_url'>>
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('log_entries').update(fields).eq('id', id)
  if (error) return { error: error.message }
  return { error: null }
}

export async function getLogEntryById(id: string): Promise<{ data: DiaryEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('log_entries')
    .select('*, productions(*, plays(id, title, category))')
    .eq('id', id)
    .maybeSingle()
  if (error) return { data: null, error: error.message }
  return { data: data as DiaryEntry | null, error: null }
}

export async function insertLogEntry(
  userId: string,
  entry: NewLogEntry
): Promise<{ data: LogEntry | null; error: string | null }> {
  const { data, error } = await supabase
    .from('log_entries')
    .insert({ user_id: userId, ...entry })
    .select()
    .single()

  if (error) return { data: null, error: error.message }
  return { data, error: null }
}

export function useLogEntries(userId: string | undefined) {
  const [logEntries, setLogEntries] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    async function fetchLogEntries() {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('log_entries')
        .select('*')
        .eq('user_id', userId)
        .order('date_watched', { ascending: false })

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setLogEntries(data ?? [])
      }

      setLoading(false)
    }

    fetchLogEntries()
  }, [userId])

  return { logEntries, loading, error }
}

export function useProfileDiaryEntries(userId: string | undefined) {
  const PAGE_SIZE = 20
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    async function fetchPage() {
      const from = page * PAGE_SIZE
      const to = from + PAGE_SIZE - 1

      const { data, error: supabaseError, count } = await supabase
        .from('log_entries')
        .select('*, productions(*, plays(id, title, category))', { count: 'exact' })
        .eq('user_id', userId)
        .order('date_watched', { ascending: false })
        .range(from, to)

      if (cancelled) return

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        const fetched = (data ?? []) as DiaryEntry[]
        setEntries(prev => page === 0 ? fetched : [...prev, ...fetched])
        setHasMore(fetched.length === PAGE_SIZE)
        if (page === 0) setTotalCount(count ?? 0)
      }
      setLoading(false)
    }

    fetchPage()
    return () => { cancelled = true }
  }, [userId, page])

  const loadMore = () => setPage(p => p + 1)
  const removeEntry = (id: string) => setEntries(prev => prev.filter(e => e.id !== id))

  return { entries, loading, error, hasMore, totalCount, loadMore, removeEntry }
}

export function useLogEntry(id: string) {
  const [logEntry, setLogEntry] = useState<LogEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    async function fetchLogEntry() {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('log_entries')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (supabaseError) {
        setError(supabaseError.message)
      } else {
        setLogEntry(data)
      }

      setLoading(false)
    }

    fetchLogEntry()
  }, [id])

  return { logEntry, loading, error }
}
